import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ShoppingBag, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useCart } from '../context/CartContext';
import { MiniCartPreview } from './MarketFeatures';

const LOGO_URL = '/images/kosad-logo.png';

const navItems = [
  { name: 'Ana Sayfa', path: '/' },
  {
    name: 'Hakkımızda',
    path: '/hakkimizda',
    children: [
      { name: 'Hikayemiz', path: '/hakkimizda#hikaye' },
      { name: 'Logo Hikayemiz', path: '/hakkimizda#logo-hikaye' },
      { name: 'Vizyon & Misyon', path: '/hakkimizda#vizyon' },
      { name: 'Değerlerimiz', path: '/hakkimizda#degerler' },
      { name: 'Ekibimiz', path: '/hakkimizda#ekip' },
    ]
  },
  { name: 'Çalışmalarımız', path: '/calismalarimiz' },
  { name: 'Eğitim', path: '/egitim' },
  { name: 'Haberler', path: '/haberler' },
  { name: 'Katıl', path: '/katil' },
  { name: 'Market', path: '/market', badge: 'YENİ' },
  { name: 'İletişim', path: '/iletisim' },
];

export const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [showMiniCart, setShowMiniCart] = useState(false);
  const location = useLocation();
  const { items, itemCount, total } = useCart();

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 header-glass" data-testid="main-header">
      <div className="container-custom">
        <div className="flex items-center justify-between h-24">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0" data-testid="logo-link">
            <img 
              src={LOGO_URL} 
              alt="KOSAD" 
              className="h-20 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
            {navItems.map((item) => (
              <div key={item.name} className="relative group">
                {item.children ? (
                  <button
                    className={`px-4 py-2 text-base font-medium flex items-center gap-1 transition-colors ${
                      isActive(item.path) ? 'text-[#1A3625]' : 'text-[#5C5C5C] hover:text-[#1A3625]'
                    }`}
                    onMouseEnter={() => setOpenDropdown(item.name)}
                    onMouseLeave={() => setOpenDropdown(null)}
                  >
                    {item.name}
                    <ChevronDown className="w-4 h-4" />
                  </button>
                ) : (
                  <Link
                    to={item.path}
                    className={`px-3 py-2 text-[15px] font-medium transition-colors flex items-center gap-2 ${
                      isActive(item.path) ? 'text-[#1A3625]' : 'text-[#5C5C5C] hover:text-[#1A3625]'
                    }`}
                    data-testid={`nav-${item.name.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    {item.name}
                    {item.badge && (
                      <span className="bg-[#D4AF37] text-[#1A3625] text-xs font-bold px-2 py-0.5 rounded-full">
                        {item.badge}
                      </span>
                    )}
                  </Link>
                )}

                {/* Dropdown */}
                {item.children && (
                  <AnimatePresence>
                    {openDropdown === item.name && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        className="absolute top-full left-0 w-48 bg-white rounded-2xl shadow-lg py-2 mt-2"
                        onMouseEnter={() => setOpenDropdown(item.name)}
                        onMouseLeave={() => setOpenDropdown(null)}
                      >
                        {item.children.map((child) => (
                          <Link
                            key={child.name}
                            to={child.path}
                            className="block px-4 py-2 text-base text-[#5C5C5C] hover:text-[#1A3625] hover:bg-[#F9F8F6] transition-colors"
                          >
                            {child.name}
                          </Link>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                )}
              </div>
            ))}
          </nav>

          {/* Right side - Cart and Mobile Menu */}
          <div className="flex items-center gap-4">
            {/* Cart with Mini Preview */}
            <div className="relative">
              <button
                onClick={() => setShowMiniCart(!showMiniCart)}
                onMouseEnter={() => setShowMiniCart(true)}
                className="relative p-2 hover:bg-[#1A3625]/5 rounded-full transition-colors"
                data-testid="cart-button"
              >
                <ShoppingBag className="w-6 h-6 text-[#1A3625]" />
                {itemCount > 0 && (
                  <motion.span 
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="absolute -top-1 -right-1 w-5 h-5 bg-[#D4AF37] text-white text-xs font-medium flex items-center justify-center rounded-full"
                  >
                    {itemCount}
                  </motion.span>
                )}
              </button>
              
              {/* Mini Cart Preview */}
              <div onMouseLeave={() => setShowMiniCart(false)}>
                <MiniCartPreview 
                  isOpen={showMiniCart} 
                  onClose={() => setShowMiniCart(false)}
                  items={items}
                  total={total}
                />
              </div>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2 hover:bg-[#1A3625]/5 rounded-full transition-colors"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              data-testid="mobile-menu-button"
            >
              {isMenuOpen ? (
                <X className="w-6 h-6 text-[#1A3625]" />
              ) : (
                <Menu className="w-6 h-6 text-[#1A3625]" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-t border-[#E5DAC8]"
            data-testid="mobile-menu"
          >
            <nav className="container-custom py-4">
              {navItems.map((item) => (
                <div key={item.name}>
                  <Link
                    to={item.path}
                    className={`block py-3 text-lg font-medium flex items-center gap-2 ${
                      isActive(item.path) ? 'text-[#1A3625]' : 'text-[#5C5C5C]'
                    }`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {item.name}
                    {item.badge && (
                      <span className="bg-[#D4AF37] text-[#1A3625] text-xs font-bold px-2 py-0.5 rounded-full">
                        {item.badge}
                      </span>
                    )}
                  </Link>
                  {item.children && (
                    <div className="pl-4">
                      {item.children.map((child) => (
                        <Link
                          key={child.name}
                          to={child.path}
                          className="block py-2 text-base text-[#8C7A6B]"
                          onClick={() => setIsMenuOpen(false)}
                        >
                          {child.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
